import { Controller } from "@hotwired/stimulus";

// Loads a turbo-frame the first time its enclosing <details> is opened.
//
// NOT loading="lazy". A turbo-frame inside a CLOSED <details> still fetches — verified in a browser
// spec, where the frame's `complete` attribute was already true on page load with the card collapsed.
// So lazy loading would fire one request per collapsed card, which is worse than the eager rendering
// it was meant to replace: the experiments report computes every experiment's metrics in ONE pair of
// ClickHouse scans, and a request per card turns that into a query per card.
//
// The <details> `toggle` event is the signal that actually means "the reader wants to see this". It
// does not bubble, hence the listener on the element itself rather than delegation.
export default class extends Controller {
  static targets = ["frame"];
  static values = { src: String };

  connect() {
    this.details = this.element.closest("details");
    if (!this.details) return;

    this.onToggle = this.onToggle.bind(this);
    this.details.addEventListener("toggle", this.onToggle);

    // The two failures Turbo reports (a dead network, and HTML with no matching frame) share one
    // handler, because the recovery is the same: load() refuses to act once src is set, so without
    // clearing it a failed card is stuck for the life of the page. Both bubble, hence one listener
    // on the wrapper. There is a third failure Turbo reports as NOTHING — see docs/turbo-frames.md,
    // "Error Responses" — which no listener here can help with; it is fixed at the endpoint.
    this.onLoadFailure = this.onLoadFailure.bind(this);
    this.element.addEventListener("turbo:fetch-request-error", this.onLoadFailure);
    this.element.addEventListener("turbo:frame-missing", this.onLoadFailure);

    // A card rendered already-open (or restored open from a Turbo cache) should not wait for a toggle
    // that will never come.
    if (this.details.open) this.load();
  }

  disconnect() {
    if (this.details && this.onToggle) {
      this.details.removeEventListener("toggle", this.onToggle);
    }
    if (this.onLoadFailure) {
      this.element.removeEventListener("turbo:fetch-request-error", this.onLoadFailure);
      this.element.removeEventListener("turbo:frame-missing", this.onLoadFailure);
    }
  }

  onLoadFailure(event) {
    // Cancel ONLY frame-missing, where Turbo's default is to overwrite the frame with "Content
    // missing" and throw. Cancelling fetch-request-error instead makes willDelegateErrorHandling
    // return false, which skips requestErrored entirely — losing the console.error that is the only
    // trace of a transient network failure, and leaving the frame's load promise unresolved.
    if (event.type === "turbo:frame-missing") event.preventDefault();
    if (!this.hasFrameTarget) return;

    // Clearing src is what hands the next toggle a retry.
    this.frameTarget.removeAttribute("src");
    // Say so, rather than leaving the loader spinning at a card that is never going to fill in.
    // Naming the gesture matters because reopening is the retry and nothing else suggests that.
    this.frameTarget.innerHTML =
      '<div class="card-body"><p class="text-sm text-sg-gray-500 mb-0">' +
      "Couldn&rsquo;t load this comparison. Close and reopen the card to try again." +
      "</p></div>";
  }

  onToggle() {
    if (this.details.open) this.load();
  }

  // Setting src is what triggers Turbo to fetch. Guarded on src already being present so reopening a
  // card doesn't refetch what it already holds.
  load() {
    if (!this.hasFrameTarget || this.frameTarget.getAttribute("src")) return;

    this.frameTarget.setAttribute("src", this.srcValue);
  }
}
